import React, { useCallback, useState } from 'react';
import { BRAND, getCategoryLabel, BAND_COLORS, IST_ZUSTAND_CATEGORIES, VERHALTEN_CATEGORIES, scoreToBand, } from './types';
import { ScoreRing } from './ScoreRing';
import { ProgressBar } from './ProgressBar';
import { CategoryDetailPanel } from './CategoryDetailPanel';
import { selectMeasurementTargets } from './selectMeasurementTargets';
import { uiText } from './types';
// ── Leaf components (shared with trainer cockpit) ─────────────────────────────
function BandDot({ score }) {
    return <span className="inline-block rounded-full w-2.5 h-2.5 flex-shrink-0" style={{ backgroundColor: BAND_COLORS[scoreToBand(score)] }}/>;
}
function MeasuredBadge() {
    return (<span className="inline-flex items-center gap-1 whitespace-nowrap shrink-0 text-xs font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: BRAND.tint, color: BRAND.blue }}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <circle cx="5" cy="5" r="4" stroke="currentColor" strokeWidth="1.5"/>
                <circle cx="5" cy="5" r="1.5" fill="currentColor"/>
            </svg>
            Gemessen
        </span>);
}
function LeverBadge({ locale }) {
    return (<span className="text-xs font-bold px-1.5 py-0.5 rounded whitespace-nowrap shrink-0" style={{ backgroundColor: BRAND.blue, color: '#fff' }}>{uiText('lever.badge', locale)}</span>);
}
function IstZustandRow({ category, entry, isFocus, isLever, onSelect, locale }) {
    const text = entry.interpretation?.trainer ?? null;
    return (<button type="button" onClick={onSelect} className="w-full text-left rounded-lg p-3 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1" style={isFocus
            ? { borderLeft: `3px solid ${BRAND.blue}`, backgroundColor: BRAND.tint, paddingLeft: 12 }
            : { borderLeft: '3px solid transparent' }}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-2">
                <BandDot score={entry.score}/>
                <span className="text-sm font-medium text-gray-800 flex-1">{getCategoryLabel(category, locale)}</span>
                {isLever && <LeverBadge locale={locale}/>}
                {entry.trainerScore !== undefined && <MeasuredBadge />}
                <span className="text-sm font-bold ml-1" style={{ color: BAND_COLORS[scoreToBand(entry.score)] }}>{entry.score}</span>
            </div>
            <ProgressBar score={entry.score} height={6}/>
            {text !== null && <p className="mt-1.5 text-xs text-gray-500 leading-snug line-clamp-2">{text}</p>}
        </button>);
}
function VerhaltenTile({ category, entry, isFocus, isLever, onSelect, locale }) {
    const text = entry.interpretation?.trainer ?? null;
    return (<button type="button" onClick={onSelect} className="flex flex-col items-center gap-2 bg-white rounded-2xl p-4 transition-shadow hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-1 relative" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', ...(isFocus ? { outline: `2px solid ${BRAND.blue}` } : {}) }}>
            {isLever && <span className="absolute top-2 right-2"><LeverBadge locale={locale}/></span>}
            <ScoreRing score={entry.score} size={80} strokeWidth={8}/>
            <div className="text-center">
                <div className="flex items-center gap-1 justify-center">
                    <BandDot score={entry.score}/>
                    <span className="text-xs font-semibold text-gray-700">{getCategoryLabel(category, locale)}</span>
                </div>
                {text !== null && <p className="mt-1 text-xs text-gray-500 leading-snug line-clamp-2 text-center">{text}</p>}
            </div>
        </button>);
}
export function MeasurementTargetList({ targets, leverCategory, locale = 'de' }) {
    const selected = selectMeasurementTargets(targets, leverCategory);
    if (selected.length === 0) {
        return null;
    }
    return (<div className="w-full flex flex-col gap-1 border-t border-gray-100 pt-3">
            <p className="text-xs text-gray-400 mb-0.5">{uiText('measurementTargets.title', locale)}</p>
            {selected.map(t => (<div key={t.label} className="flex items-baseline gap-1 text-xs">
                    <span className="text-gray-500 truncate flex-1">{t.label}</span>
                    <span className="font-bold shrink-0">{t.currentValue.toFixed(1)}</span>
                    {t.targetValue !== null ? (<>
                            <span className="text-gray-300">→</span>
                            <span className="text-green-600 font-bold shrink-0">{t.targetValue.toFixed(1)}</span>
                            {t.unit !== null && <span className="text-gray-400">{t.unit}</span>}
                        </>) : (<>
                            {t.unit !== null && <span className="text-gray-400">{t.unit}</span>}
                            <span className="text-green-600 shrink-0" title={uiText('measurementTargets.reached', locale)}>✓</span>
                        </>)}
                </div>))}
        </div>);
}
function LeverColumn({ title, category, entry, onSelect, locale }) {
    return (<button type="button" onClick={onSelect} className="flex flex-col items-center gap-2 rounded-xl p-1 transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1">
            {/* Reserve two text lines so a wrapping title never pushes its ring
            below the neighbour lever's. */}
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 text-center min-h-[2rem] flex items-center justify-center">{title}</p>
            {entry !== undefined && <ScoreRing score={entry.score} size={120} strokeWidth={10}/>}
            <p className="text-sm font-bold text-center" style={{ color: BRAND.blueDeep }}>
                {getCategoryLabel(category, locale)}
            </p>
        </button>);
}
// One HebelCard for every cockpit surface (trainer app, Mein-Bereich,
// healthscore-api) — both levers side by side, measurement targets scoped to the
// Ist-Zustand lever underneath. The surfaces drifted when each kept its
// own copy.
export function LeverCard({ currentStateCategory, behaviorCategory = null, categoryScores, targets, onSelectCategory, locale = 'de' }) {
    if (currentStateCategory === null && behaviorCategory === null) {
        return (<div className="w-full h-full bg-white rounded-2xl p-5 flex flex-col items-center justify-center gap-2 border-l-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderLeftColor: BRAND.blue }}>
                <p className="text-xs text-gray-400">{uiText('lever.none', locale)}</p>
            </div>);
    }
    return (<div className="w-full h-full bg-white rounded-2xl p-5 flex flex-col justify-between gap-4 border-l-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderLeftColor: BRAND.blue }}>
            <div className="flex flex-row items-start justify-around gap-4">
                {currentStateCategory !== null
            ? <LeverColumn title={uiText('lever.currentStateTitle', locale)} category={currentStateCategory} entry={categoryScores[currentStateCategory]} onSelect={() => onSelectCategory(currentStateCategory)} locale={locale}/>
            : <p className="text-xs text-gray-400 self-center">{uiText('lever.noCurrentState', locale)}</p>}
                {behaviorCategory !== null
            ? <LeverColumn title={uiText('lever.behaviorTitle', locale)} category={behaviorCategory} entry={categoryScores[behaviorCategory]} onSelect={() => onSelectCategory(behaviorCategory)} locale={locale}/>
            : <p className="text-xs text-gray-400 self-center">{uiText('lever.noBehavior', locale)}</p>}
            </div>
            <MeasurementTargetList targets={targets} leverCategory={currentStateCategory} locale={locale}/>
        </div>);
}
function Shimmer({ className }) {
    return <div className={`animate-pulse bg-gray-100 rounded ${className ?? ''}`}/>;
}
function ScoreRingPlaceholder({ size = 130 }) {
    return (<div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
            <svg width={size} height={size} aria-hidden="true">
                <circle cx={size / 2} cy={size / 2} r={(size - 13) / 2} fill="none" stroke="#e5e7eb" strokeWidth={13}/>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5">
                <Shimmer className="w-12 h-7"/>
                <Shimmer className="w-16 h-3"/>
            </div>
        </div>);
}
function IstZustandRowPlaceholder({ category, locale }) {
    return (<div className="rounded-lg p-3" style={{ borderLeft: '3px solid transparent' }}>
            <div className="flex items-center gap-2 mb-1.5">
                <Shimmer className="w-2.5 h-2.5 rounded-full"/>
                <span className="text-sm font-medium text-gray-300">{getCategoryLabel(category, locale)}</span>
            </div>
            <div className="w-full rounded-full overflow-hidden bg-gray-100" style={{ height: 6 }}>
                <div className="h-full rounded-full bg-gray-100" style={{ width: '100%' }}/>
            </div>
        </div>);
}
function VerhaltenTilePlaceholder({ category, locale }) {
    return (<div className="flex flex-col items-center gap-2 rounded-xl p-4" style={{ border: '1px solid #e5e7eb' }}>
            <div className="relative flex items-center justify-center" style={{ width: 80, height: 80 }}>
                <svg width={80} height={80} aria-hidden="true">
                    <circle cx={40} cy={40} r={36} fill="none" stroke="#e5e7eb" strokeWidth={8}/>
                </svg>
                <Shimmer className="absolute w-8 h-5"/>
            </div>
            <div className="text-center">
                <span className="text-xs font-semibold text-gray-300">{getCategoryLabel(category, locale)}</span>
            </div>
        </div>);
}
function LeverCardPlaceholder() {
    return (<div className="bg-white rounded-2xl p-5 flex flex-col items-center justify-center gap-3 border-l-4" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', borderLeftColor: '#d1dfe8' }}>
            <Shimmer className="w-24 h-2.5"/>
            <ScoreRingPlaceholder size={130}/>
            <Shimmer className="w-28 h-4"/>
        </div>);
}
export function HealthscoreCockpitView({ categoryScores, overallScore, leverCategory = null, behaviorLeverCategory = null, focusCategories = [], measurementTargets = [], reports = [], fragebogenOnly = false, audience = 'trainer', locale = 'de', }) {
    const [selectedCategory, setSelectedCategory] = useState(null);
    // Stopgap: reports generated before the ingest fix can carry duplicate targets
    // for the same measurement (repeat submits into one open session). Keep the last
    // per categoryKey so the cockpit never shows a measurement more than once.
    const dedupedTargets = Object.values(measurementTargets.reduce((acc, t) => {
        acc[t.categoryKey] = t;
        return acc;
    }, {}));
    const handleCategorySelect = useCallback((cat) => {
        setSelectedCategory(prev => prev === cat ? null : cat);
    }, []);
    const istZustandPresent = IST_ZUSTAND_CATEGORIES.filter(c => categoryScores[c] !== undefined);
    const verhaltenPresent = VERHALTEN_CATEGORIES.filter(c => categoryScores[c] !== undefined);
    const selectedEntry = selectedCategory === '__overall__' && overallScore !== null
        ? { score: overallScore, source: 'overall' }
        : selectedCategory !== null
            ? categoryScores[selectedCategory]
            : undefined;
    const filteredTargets = selectedEntry?.includedKeys !== undefined
        ? dedupedTargets.filter(t => selectedEntry.includedKeys.includes(t.categoryKey))
        : [];
    const hasData = Object.keys(categoryScores).length > 0;
    return (<div className="flex flex-col gap-6">
            <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 1.4fr 1fr' }}>
                <button type="button" onClick={() => { if (hasData && overallScore !== null)
        handleCategorySelect('__overall__'); }} disabled={!hasData || overallScore === null} className="bg-white rounded-2xl p-5 flex flex-col items-center justify-center gap-3 transition-shadow focus:outline-none focus:ring-2 focus:ring-offset-2" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)', cursor: hasData && overallScore !== null ? 'pointer' : 'default' }}>
                    {hasData && overallScore !== null
            ? <ScoreRing score={overallScore} size={130} strokeWidth={13} label={uiText('score.overall', locale)}/>
            : <ScoreRingPlaceholder size={130}/>}
                    {fragebogenOnly && (<p className="text-xs text-gray-400 text-center">{uiText('score.questionnaireOnly', locale)}</p>)}
                </button>

                <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.10)' }}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1 px-1">{uiText('section.currentState', locale)}</h3>
                    <div className="flex flex-col gap-1">
                        {hasData
            ? istZustandPresent.map(cat => (<IstZustandRow key={cat} category={cat} entry={categoryScores[cat]} isFocus={focusCategories.includes(cat)} isLever={leverCategory === cat} onSelect={() => handleCategorySelect(cat)} locale={locale}/>))
            : IST_ZUSTAND_CATEGORIES.map(cat => <IstZustandRowPlaceholder key={cat} category={cat} locale={locale}/>)}
                    </div>
                </div>

                {hasData
            ? <LeverCard currentStateCategory={leverCategory} behaviorCategory={behaviorLeverCategory} categoryScores={categoryScores} targets={dedupedTargets} onSelectCategory={handleCategorySelect} locale={locale}/>
            : <LeverCardPlaceholder />}
            </div>

            <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3 px-1">{uiText('section.behavior', locale)}</h3>
                <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                    {hasData
            ? verhaltenPresent.map(cat => (<VerhaltenTile key={cat} category={cat} entry={categoryScores[cat]} isFocus={focusCategories.includes(cat)} isLever={leverCategory === cat} onSelect={() => handleCategorySelect(cat)} locale={locale}/>))
            : VERHALTEN_CATEGORIES.map(cat => <VerhaltenTilePlaceholder key={cat} category={cat} locale={locale}/>)}
                </div>
            </div>

            {selectedCategory !== null && selectedEntry !== undefined && (<CategoryDetailPanel category={selectedCategory} entry={selectedEntry} measurementTargets={filteredTargets} reports={reports} audience={audience} locale={locale} onClose={() => setSelectedCategory(null)}/>)}
        </div>);
}
